import { useState, useEffect, useRef } from "react";
import { StripeCardForm } from "./StripeCardForm";
import { API } from "./calendarUtils";

const PASS_OPTIONS = [
  { id: "dropin", label: "Single Class",  classes: 1, display: 25,  stripeAmount: 2606  },
  { id: "pass4",  label: "4-Class Pass",  classes: 4, display: 84,  stripeAmount: 8682  },
  { id: "pass8",  label: "8-Class Pass",  classes: 8, display: 152, stripeAmount: 15685 },
];

const MEMBERSHIP_OPTIONS = [
  { id: "member2x",  label: "Committed",         sub: "2 classes per week", display: 129, actualCharge: 133.16 },
  { id: "memberUnl", label: "Monthly", sub: "One class per day",  display: 169, actualCharge: 174.36 },
];

const isMembershipType = (id) => MEMBERSHIP_OPTIONS.some(o => o.id === id);

export function PassPurchaseModal({ onClose, prefillName = "", prefillEmail = "", prefillPassType = "pass4", onPurchaseComplete, justSigned = false }) {
  const initialMode     = isMembershipType(prefillPassType) ? "membership" : "pass";
  const initialSelected = initialMode === "membership"
    ? (isMembershipType(prefillPassType) ? prefillPassType : "member2x")
    : (PASS_OPTIONS.find(o => o.id === prefillPassType) ? prefillPassType : "pass4");

  const [mode, setMode]             = useState(initialMode);
  const [selected, setSelected]     = useState(initialSelected);
  const [name, setName]             = useState(prefillName);
  const [email, setEmail]           = useState(prefillEmail);
  const [error, setError]           = useState(null);
  const [purchased, setPurchased]   = useState(null);
  const [subscribed, setSubscribed] = useState(null);
  const [memberClientSecret, setMemberClientSecret] = useState(null);
  const [fetchingSecret, setFetchingSecret]         = useState(false);
  const [agreementSigned, setAgreementSigned]       = useState(justSigned);
  const cardRef     = useRef(null);
  const autoStarted = useRef(false);
  const [checkingAgreement, setCheckingAgreement]   = useState(false);

  const options      = mode === "membership" ? MEMBERSHIP_OPTIONS : PASS_OPTIONS;
  const chosenPass   = options.find(p => p.id === selected) || options[0];
  const isMembership = mode === "membership";
  const nameValid    = name.trim().length >= 2;
  const emailValid   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    if (!isMembership || !emailValid) return;
    fetch(`${API}/membership-agreement/status?email=${encodeURIComponent(email.trim())}&type=${selected}`)
      .then(r => r.json())
      .then(data => setAgreementSigned(data.signed ?? false))
      .catch(() => {});
  }, [email, selected, isMembership, emailValid]);

  // Just back from signing: go straight to the card form
  useEffect(() => {
    if (autoStarted.current) return;
    autoStarted.current = true;
    if (justSigned && isMembership && nameValid && emailValid) handleMembershipSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memberClientSecret) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [memberClientSecret]);

  const handleMembershipClick = async () => {
    if (!nameValid || !emailValid) { setError("Please enter your full name and a valid email."); return; }
    if (!agreementSigned) {
      setCheckingAgreement(true);
      try {
        const res  = await fetch(`${API}/membership-agreement/status?email=${encodeURIComponent(email.trim())}&type=${selected}`);
        const data = await res.json();
        if (!data.signed) {
          const params = new URLSearchParams({ type: selected, name: name.trim(), email: email.trim() });
          window.location.href = `/membership-agreement?${params.toString()}`;
          return;
        }
        setAgreementSigned(true);
      } catch {
        setError("Could not verify agreement status. Please try again.");
        return;
      } finally {
        setCheckingAgreement(false);
      }
    }
    handleMembershipSetup();
  };

  const handleMembershipSetup = async () => {
    if (!nameValid || !emailValid) { setError("Please enter your full name and a valid email."); return; }
    setFetchingSecret(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/passes/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), passType: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Subscription setup failed.");
      setMemberClientSecret(data.clientSecret);
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchingSecret(false);
    }
  };

  const handlePassSuccess = async (paymentIntentId) => {
    try {
      const res  = await fetch(`${API}/passes/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), email: email.trim(),
          passType: selected, stripePaymentId: paymentIntentId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Purchase failed.");
      setPurchased(data.pass);
      onPurchaseComplete?.(data.pass.code);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMemberSuccess = () => {
    setSubscribed({
      type: selected, label: chosenPass.label,
      price: chosenPass.display,
      actualCharge: chosenPass.actualCharge ?? chosenPass.display,
      email: email.trim(),
    });
      // Poll for the pass code once webhook creates it
  let attempts = 0;
  const poll = setInterval(async () => {
    attempts++;
    try {
      const res = await fetch(`${API}/passes/active?email=${encodeURIComponent(email.trim())}`);
      const data = await res.json();
      if (data.pass?.code) {
        clearInterval(poll);
        onPurchaseComplete?.(data.pass.code);
      } else if (attempts >= 7) {
        clearInterval(poll);
        onPurchaseComplete?.(null); // fallback — let student enter code manually
      }
    } catch {
      clearInterval(poll);
      onPurchaseComplete?.(null);
    }
  }, 1500);
};

  if (purchased) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">Pass confirmed!</h2>
            <p className="booking-success-sub">Your pass code has been emailed to you.</p>
            <div className="pass-purchase-code">{purchased.code}</div>
            <p className="booking-success-sub" style={{fontSize:"0.85rem", opacity:0.7}}>
              {purchased.classesTotal} class{purchased.classesTotal !== 1 ? "es" : ""}
              {purchased.expiresAt
                ? ` · expires ${new Date(purchased.expiresAt).toLocaleDateString("en-US", {month:"long", day:"numeric", year:"numeric"})}`
                : ""}
            </p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're a member!</h2>
            <p className="booking-success-sub">
              Welcome to the {subscribed.label}. Your member code and welcome details
              are on their way to <strong>{subscribed.email}</strong>.
            </p>
            <p className="booking-success-sub" style={{fontSize:"0.85rem", opacity:0.7, marginTop:"0.75rem"}}>
              Your card will be charged ${subscribed.actualCharge.toFixed(2)} automatically each month
              (includes card processing fee) until you cancel. Manage your membership anytime
              from <strong>My Bookings</strong>.
            </p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2 className="modal-title">{isMembership ? "Become a Member" : "Buy a Pass"}</h2>

        {isMembership && agreementSigned && (
          <p className="pass-feedback valid" style={{marginBottom:"1rem"}}>
            {justSigned
              ? <>✓ Agreement signed. A copy has been emailed to <strong>{email.trim()}</strong>. Enter your card below to finish.</>
              : "✓ Membership agreement on file"}
          </p>
        )}

        <div className="pay-mode-tabs" style={{marginBottom:"1rem"}}>
          <button type="button"
            className={`pay-mode-tab ${!isMembership ? "selected" : ""}`}
            onClick={() => { setMode("pass"); setSelected(PASS_OPTIONS.find(o => o.id === selected)?.id || "pass4"); setError(null); setMemberClientSecret(null); }}>
            One-time pass
          </button>
          <button type="button"
            className={`pay-mode-tab ${isMembership ? "selected" : ""}`}
            onClick={() => { setMode("membership"); setSelected(isMembershipType(selected) ? selected : "member2x"); setError(null); setMemberClientSecret(null); }}>
            Monthly membership
          </button>
        </div>

        <div className="pass-purchase-options">
          {options.map(opt => (
            <div key={opt.id}
              className={`pass-purchase-option ${selected === opt.id ? "selected" : ""}`}
              onClick={() => { setSelected(opt.id); setMemberClientSecret(null); }}>
              <div className="pass-purchase-option-left">
                <span className="pass-purchase-option-label">{opt.label}</span>
                <span className="pass-purchase-option-sub">
                  {isMembership ? opt.sub : `${opt.classes} class${opt.classes !== 1 ? "es" : ""}`}
                </span>
              </div>
              <span className="pass-purchase-option-price">
                ${opt.display}{isMembership ? <small style={{fontWeight:400, opacity:0.7}}> /mo</small> : null}
              </span>

            </div>
          ))}
        </div>

        <div className="booking-fields" style={{margin:"1.25rem 0"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => { setName(e.target.value); setMemberClientSecret(null); }}
              placeholder="e.g. Jane Smith" minLength={2} required />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email}
              onChange={e => { setEmail(e.target.value); setMemberClientSecret(null); }} placeholder="your@email.com" />
          </label>
        </div>

        {error && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{error}</p>}

        {!isMembership && (
          <StripeCardForm
            amount={chosenPass.stripeAmount}
            email={email.trim()}
            billingDetails={{ name: name.trim(), email: email.trim() }}
            onSuccess={handlePassSuccess}
            onError={(msg) => setError(msg)}
            submitLabel={`Pay $${(chosenPass.stripeAmount / 100).toFixed(2)} & Get Pass`}
            disabled={!nameValid || !emailValid}
          />
        )}

        {isMembership && !memberClientSecret && (
          <>
            <button className="book-btn" style={{marginTop:"1.25rem"}}
              disabled={!nameValid || !emailValid || fetchingSecret || checkingAgreement}
              onClick={handleMembershipClick}>
              {checkingAgreement ? "Checking…" : fetchingSecret ? "Setting up…" : agreementSigned ? `Subscribe — $${chosenPass.actualCharge.toFixed(2)}/month` : `Sign agreement & subscribe →`}
            </button>
          </>
        )}

        {isMembership && memberClientSecret && (
          <div ref={cardRef}>
          <StripeCardForm
            clientSecret={memberClientSecret}
            email={email.trim()}
            billingDetails={{ name: name.trim(), email: email.trim() }}
            onSuccess={handleMemberSuccess}
            onError={(msg) => { setError(msg); setMemberClientSecret(null); }}
            submitLabel={`Confirm — $${(chosenPass.actualCharge ?? chosenPass.display).toFixed(2)}/month`}
          />
          </div>
        )}

        <p style={{marginTop:"0.75rem", fontSize:"0.8rem", color:"var(--color-grey-400)", textAlign:"center", lineHeight:1.5}}>
          {isMembership
            ? <>Your card will be charged <strong>${(chosenPass.actualCharge ?? chosenPass.display).toFixed(2)}</strong>/month
                (includes card processing fee) on the same day each month. Cancel anytime from My Bookings.</>
            : "Price includes card processing fee. Your pass code will be emailed immediately."
          }
        </p>
      </div>
    </div>
  );
}
